import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ExternalProductFilterDropdown from 'src/sections/catalog_products/ExternalProductFilterDropdown';
import ProductToolbarActions from 'src/sections/catalog_products/ProductToolbarActions';
import { ViewCatalogProductsContext } from 'src/sections/catalog_products/ViewCatalogProductsContext';
import { useContextSelector } from 'use-context-selector';

interface CardGridHeaderProps {}
interface SortOption {
  value: string;
  label: string;
}

const baseSortOptions: SortOption[] = [
  { value: 'itemNum-asc', label: 'Item # - Ascending' },
  { value: 'itemNum-desc', label: 'Item # - Descending' },
  { value: 'description-asc', label: 'Description - Ascending' },
  { value: 'description-desc', label: 'Description - Descending' },
  { value: 'price-asc', label: 'Price - Ascending' },
  { value: 'price-desc', label: 'Price - Descending' },
];

const supplierSortOptions: SortOption[] = [
  { value: 'companyName-asc', label: 'Supplier - Ascending' },
  { value: 'companyName-desc', label: 'Supplier - Descending' },
];

const CardGridHeader: FC<CardGridHeaderProps> = () => {
  const { t } = useTranslation();
  // Get dataState, onDataStateChange, and isOfflineCatalog from context
  const dataState = useContextSelector(ViewCatalogProductsContext, (state) => state.dataState);
  const onDataStateChange = useContextSelector(
    ViewCatalogProductsContext,
    (state) => state.onDataStateChange
  );
  const isOfflineCatalog = useContextSelector(
    ViewCatalogProductsContext,
    (state) => state.isOfflineCatalog
  );

  // Build translated sort options based on catalog type
  const translatedBaseSortOptions = useMemo(
    () => baseSortOptions.map((opt) => ({ ...opt, label: t(opt.label) })),
    [t]
  );
  const translatedSupplierSortOptions = useMemo(
    () => supplierSortOptions.map((opt) => ({ ...opt, label: t(opt.label) })),
    [t]
  );
  const sortOptions = useMemo(
    () =>
      isOfflineCatalog
        ? [...translatedBaseSortOptions, ...translatedSupplierSortOptions]
        : translatedBaseSortOptions,
    [isOfflineCatalog, translatedBaseSortOptions, translatedSupplierSortOptions]
  );

  // Map sort dropdown value to field name
  const getSortField = (value: string): string => {
    if (value.startsWith('itemNum-')) return 'itemNum';
    if (value.startsWith('description-')) return 'description'; // description is the description field
    if (value.startsWith('price-')) return 'price';
    if (value.startsWith('companyName-')) return 'companyName';
    return 'itemNum';
  };

  // Map sort dropdown value to direction
  const getSortDir = (value: string): 'asc' | 'desc' => (value.endsWith('-asc') ? 'asc' : 'desc');

  // Get current sort value from dataState
  const selectedSort = useMemo(() => {
    const [defaultOption] = sortOptions;

    if (!dataState?.sort || dataState.sort.length === 0) {
      return defaultOption; // Default to first option
    }
    const [currentSort] = dataState.sort;
    const { field, dir = 'asc' } = currentSort;

    // Map field name back to dropdown value
    let value = '';
    if (field === 'itemNum') {
      value = `itemNum-${dir}`;
    } else if (field === 'description') {
      value = `description-${dir}`;
    } else if (field === 'price') {
      value = `price-${dir}`;
    } else if (field === 'companyName' && isOfflineCatalog) {
      value = `companyName-${dir}`;
    } else {
      // If companyName is selected but not an offline catalog, fall back to default
      return defaultOption;
    }

    return sortOptions.find((opt) => opt.value === value) || defaultOption;
  }, [dataState?.sort, sortOptions, isOfflineCatalog]);

  const handleSortChange = useCallback(
    (e: DropDownListChangeEvent) => {
      const { value } = e;
      const sortOption = value as SortOption;
      const field = getSortField(sortOption.value);
      const dir = getSortDir(sortOption.value);

      // Update dataState with new sort
      onDataStateChange({
        ...dataState,
        sort: [{ field, dir }],
        skip: 0, // Reset to first page when sorting changes
      } as any);
    },
    [dataState, onDataStateChange]
  );

  return (
    <div className="flex w-full flex-row flex-wrap items-center justify-between gap-x-2 gap-y-2 bg-[#233843] px-2 py-1">
      <ProductToolbarActions
        showExternalProductFilter={false}
        rightContent={
          <div className="flex min-w-0 flex-row flex-wrap items-center gap-2">
            <label htmlFor="sort-by-dropdown" className="text-white text-sm whitespace-nowrap">
              {t('Sort by')}
            </label>
            <DropDownList
              id="sort-by-dropdown"
              className="k-picker-solid"
              style={{ width: 200 }}
              data={sortOptions}
              textField="label"
              dataItemKey="value"
              value={selectedSort}
              onChange={handleSortChange}
            />
            <ExternalProductFilterDropdown />
          </div>
        }
      />
    </div>
  );
};

export default CardGridHeader;
